// src/features/tasks/components/TaskForm/ExecutionSection.jsx
// How the task is carried out: procedure, manuals and completion rules.
import React, { memo, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Chip,
  Button,
  Switch,
} from '@mui/material';
import {
  ListAlt as ListAltIcon,
  Add as AddIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AddManualsDialog } from '../TaskDetailsPage/AddManualsDialog';
import { useTaskPermissions } from '../../../auth/hooks/usePermissions';
import { FormSection } from '../../../../components/common/form';

// One on/off rule as a compact card: switch, short title, one line of help.
// Clicking anywhere on the card toggles it.
function ToggleCard({ checked, onChange, title, help, disabled }) {
  return (
    <Box
      component="label"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 3,
        p: 4,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        cursor: disabled ? 'default' : 'pointer',
        transition: 'border-color 120ms',
        '&:hover': disabled ? {} : { borderColor: 'primary.main' },
      }}
    >
      <Switch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        sx={{ mt: -1.5, ml: -2 }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>{title}</Typography>
        <Typography variant="caption" color="text.secondary" component="p">{help}</Typography>
      </Box>
    </Box>
  );
}

const ExecutionSection = memo(function ExecutionSection({
  formData,
  onInputChange,
  validationErrors = {},
  loading = false,
  procedures = [],
  isCreateMode = false,
  number,
}) {
  const { t } = useTranslation(['common', 'tasks']);
  const { canCreateManuals } = useTaskPermissions();
  const [addManualsDialogOpen, setAddManualsDialogOpen] = useState(false);

  const handleFieldChange = useCallback((field) => (event) => {
    onInputChange(field, event.target.value);
  }, [onInputChange]);

  const handleAddManuals = (fileIds) => {
    if (fileIds && fileIds.length > 0) {
      const currentManuals = formData.manual_file_ids || [];
      onInputChange('manual_file_ids', Array.from(new Set([...currentManuals, ...fileIds])));
      setAddManualsDialogOpen(false);
    }
  };

  const handleRemoveManual = (fileId) => {
    onInputChange('manual_file_ids', (formData.manual_file_ids || []).filter((id) => id !== fileId));
  };

  const toggles = [
    { field: 'quick_complete', value: formData.quick_complete ?? true },
    { field: 'requires_dual_signature', value: formData.requires_dual_signature || false },
    { field: 'mark_not_completed_when_overdue', value: formData.mark_not_completed_when_overdue || false },
    { field: 'send_email', value: formData.send_email || false },
  ];

  return (
    <FormSection number={number} title={t('tasks:form.sections.execution')}>
      <FormControl fullWidth>
        <InputLabel id="execution-section-procedure-label">{t('tasks:form.execution.procedure')}</InputLabel>
        <Select
          labelId="execution-section-procedure-label"
          value={formData.procedure_id || ''}
          onChange={handleFieldChange('procedure_id')}
          disabled={loading}
          label={t('tasks:form.execution.procedure')}
          displayEmpty
          startAdornment={
            <InputAdornment position="start">
              <ListAltIcon />
            </InputAdornment>
          }
        >
          <MenuItem value="">
            <Typography color="text.secondary">{t('tasks:form.basic_info.no_procedure_linked')}</Typography>
          </MenuItem>
          {procedures.map((procedure) => (
            <MenuItem key={procedure.procedure_id} value={procedure.procedure_id}>
              {procedure.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Manuals — only when creating, and only with permission */}
      {isCreateMode && canCreateManuals && (
        <Box>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Typography variant="subtitle2" fontWeight={600} flex={1}>
              {t('tasks:labels.manuals')}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAddManualsDialogOpen(true)}
              disabled={loading}
              variant="outlined"
            >
              {t('tasks:actions.addManuals', { defaultValue: 'Add' })}
            </Button>
          </Box>
          {(formData.manual_file_ids || []).length > 0 ? (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {formData.manual_file_ids.map((fileId) => (
                <Chip
                  key={fileId}
                  icon={<DescriptionIcon />}
                  label={fileId.substring(0, 8)}
                  onDelete={() => handleRemoveManual(fileId)}
                  disabled={loading}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t('tasks:manuals.no_manuals_selected', { defaultValue: 'No manuals selected' })}
            </Typography>
          )}
        </Box>
      )}

      {/* Completion rules, two per row */}
      {/* gap 3 = 0.75rem, so each card is half the row minus half a gap; below 240px it wraps to one column */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, '& > *': { flex: '1 1 calc(50% - 0.375rem)', minWidth: 240 } }}>
        {toggles.map(({ field, value }) => (
          <ToggleCard
            key={field}
            checked={value}
            onChange={(checked) => onInputChange(field, checked)}
            title={t(`tasks:form.execution.${field}`)}
            help={t(`tasks:form.execution.${field}_help`)}
            disabled={loading}
          />
        ))}
      </Box>

      {/* Email details — only when the email action is on */}
      {formData.send_email && (
        <Box display="flex" flexDirection="column" gap={4}>
          <TextField
            fullWidth
            label={t('tasks:form.basic_info.email_recipient', { defaultValue: 'Recipient' })}
            value={formData.email_recipient || ''}
            onChange={handleFieldChange('email_recipient')}
            error={!!validationErrors.email_recipient}
            helperText={validationErrors.email_recipient}
            required
            disabled={loading}
            placeholder="name@example.com"
            type="email"
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            fullWidth
            label={t('tasks:form.basic_info.email_subject', { defaultValue: 'Subject' })}
            value={formData.email_subject || ''}
            onChange={handleFieldChange('email_subject')}
            disabled={loading}
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            fullWidth
            label={t('tasks:form.basic_info.email_body', { defaultValue: 'Body' })}
            value={formData.email_body || ''}
            onChange={handleFieldChange('email_body')}
            multiline
            rows={4}
            disabled={loading}
            inputProps={{ maxLength: 5000 }}
          />
        </Box>
      )}

      {isCreateMode && canCreateManuals && (
        <AddManualsDialog
          open={addManualsDialogOpen}
          onClose={() => setAddManualsDialogOpen(false)}
          onSubmit={handleAddManuals}
          linkedFileIds={formData.manual_file_ids || []}
          submitLoading={loading}
        />
      )}
    </FormSection>
  );
});

export default ExecutionSection;
